/**
 * Pure layout module for the Home renewal calendar dot-timeline.
 *
 * Buckets `RenewalEvent`s into 7 columns of 15-day windows (col 0 = today/past-due,
 * col 6 = +90d+), assigns each column up to 6 dots across adaptive vertical
 * lanes (per `LANES_BY_COUNT`), and exposes overflow counts, per-bucket counts,
 * and the 3 most-urgent upcoming dots. No React, no DOM, no I/O — safe to
 * unit-test as a plain function.
 *
 * Contract pinned in `docs/home-renewal-calendar-v2/DLD.md` §1-§3.
 */
import type { RenewalEvent, CalendarBucketSeverity } from "./types";
import {
  RENEWAL_DUE_SOON_DAYS,
  RENEWAL_UPCOMING_DAYS,
  RENEWAL_TIMELINE_WINDOW_DAYS,
} from "./severity";

/** Internal dot-severity labels (calendar layout only — distinct from RenewalEventSeverity). */
export type DotSeverity = CalendarBucketSeverity;

export interface DotPosition {
  id: string;
  entityId: string;
  entityName: string;
  country: string;
  countryFlag: string;
  type: "License" | "Company" | "AnnualFiling" | "EOR";
  rawValue: string;
  daysUntil: number;
  severity: DotSeverity;
  /** Horizontal position on the track, percent (4..96). */
  leftPercent: number;
  /** Vertical lane on the track — adaptive per-column count (see `LANES_BY_COUNT`). */
  topPercent: number;
  /** Hex colour matching DLD §4. */
  color: string;
}

export interface BucketCounts {
  expired: number;
  /** 0 ≤ daysUntil ≤ 30 */
  le30: number;
  /** 31 ≤ daysUntil ≤ 60 */
  le60: number;
  /** 61 ≤ daysUntil ≤ 90 */
  le90: number;
}

export interface LayoutResult {
  dots: DotPosition[];
  expiredCount: number;
  totalShown: number;
  pastDueCount: number;
  /** `{ [columnIndex]: hiddenCount }` for columns with > 6 events. */
  overflowByColumn: Record<number, number>;
  /** Counts across every event with `severity !== "none"` (overflowed events still tallied). */
  byBucket: BucketCounts;
  /** First 3 dots with `daysUntil >= 0` sorted ASC by `daysUntil`. */
  topUpcoming: DotPosition[];
}

/**
 * Adaptive lane table — index = number of dots in the column.
 * 1 dot is centred; 6 dots are evenly distributed across the track height.
 */
const LANES_BY_COUNT: ReadonlyArray<readonly number[]> = [
  [], // 0 (unused)
  [50], // 1 lone dot → centered
  [30, 70], // 2 dots
  [20, 50, 80], // 3 dots (v1 default)
  [15, 38, 62, 85], // 4 dots
  [12, 31, 50, 69, 88], // 5 dots
  [10, 26, 42, 58, 74, 90], // 6 dots (MAX_PER_COLUMN)
];

const MAX_PER_COLUMN = 6;
const COLUMN_COUNT = 7;
const MIN_LEFT_PERCENT = 4;
const MAX_LEFT_PERCENT = 96;

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function colourFor(daysUntil: number): string {
  if (daysUntil < 0) return "#ef4444";
  if (daysUntil <= RENEWAL_DUE_SOON_DAYS) return "#f59e0b";
  if (daysUntil <= RENEWAL_UPCOMING_DAYS) return "#fbbf24";
  return "#10b981";
}

function severityFor(daysUntil: number): DotSeverity {
  if (daysUntil < 0) return "expired";
  if (daysUntil <= RENEWAL_DUE_SOON_DAYS) return "le30";
  if (daysUntil <= RENEWAL_UPCOMING_DAYS) return "le60";
  return "le90";
}

/**
 * Bucket renewals into 7 columns × adaptive lanes for the 90-day dot-timeline.
 *
 * - `severity === "none"` (no parsable date) is dropped.
 * - `daysUntil < 0` collapses into column 0 at `leftPercent: 4`.
 * - Otherwise `col = clamp(floor(daysUntil/15), 0, 6)` and
 *   `leftPercent = clamp((daysUntil/90)*100, 4, 96)`.
 * - Within a column, events are sorted ASC by `daysUntil`. The first 6 render
 *   using `LANES_BY_COUNT[count]` (positional, not modulo); the rest are
 *   counted in `overflowByColumn[col]`.
 * - `byBucket` counts every input event with `severity !== "none"` regardless
 *   of overflow truncation.
 * - `topUpcoming` is `dots.filter(d => d.daysUntil >= 0)` sorted ASC by
 *   `daysUntil`, sliced to 3.
 */
export function layoutRenewals(input: RenewalEvent[]): LayoutResult {
  const columns: RenewalEvent[][] = Array.from({ length: COLUMN_COUNT }, () => []);
  const byBucket: BucketCounts = { expired: 0, le30: 0, le60: 0, le90: 0 };

  for (const ev of input) {
    if (ev.severity === "none") continue;

    if (ev.daysUntil < 0) byBucket.expired += 1;
    else if (ev.daysUntil <= RENEWAL_DUE_SOON_DAYS) byBucket.le30 += 1;
    else if (ev.daysUntil <= RENEWAL_UPCOMING_DAYS) byBucket.le60 += 1;
    else if (ev.daysUntil <= RENEWAL_TIMELINE_WINDOW_DAYS) byBucket.le90 += 1;

    const col =
      ev.daysUntil < 0 ? 0 : Math.min(COLUMN_COUNT - 1, Math.floor(ev.daysUntil / 15));
    columns[col]!.push(ev);
  }

  const dots: DotPosition[] = [];
  const overflowByColumn: Record<number, number> = {};
  let expiredCount = 0;
  let pastDueCount = 0;

  for (let col = 0; col < COLUMN_COUNT; col += 1) {
    const bucket = columns[col]!;
    bucket.sort((a, b) => a.daysUntil - b.daysUntil);

    const visible = Math.min(bucket.length, MAX_PER_COLUMN);
    const lanes = LANES_BY_COUNT[visible]!;

    for (let i = 0; i < bucket.length; i += 1) {
      const ev = bucket[i]!;
      if (ev.daysUntil < 0) {
        pastDueCount += 1;
        // All past-due events count as expired (backend severity is never "expired")
        expiredCount += 1;
      }

      if (i >= MAX_PER_COLUMN) {
        overflowByColumn[col] = (overflowByColumn[col] ?? 0) + 1;
        continue;
      }

      const lane = lanes[i]!;
      const leftPercent =
        ev.daysUntil < 0
          ? MIN_LEFT_PERCENT
          : clamp(
              (ev.daysUntil / RENEWAL_TIMELINE_WINDOW_DAYS) * 100,
              MIN_LEFT_PERCENT,
              MAX_LEFT_PERCENT,
            );

      dots.push({
        id: ev.id,
        entityId: ev.entityId,
        entityName: ev.entityName,
        country: ev.country,
        countryFlag: ev.countryFlag,
        type: ev.type,
        rawValue: ev.rawValue,
        daysUntil: ev.daysUntil,
        severity: severityFor(ev.daysUntil),
        leftPercent,
        topPercent: lane,
        color: colourFor(ev.daysUntil),
      });
    }
  }

  const topUpcoming = dots
    .filter((d) => d.daysUntil >= 0)
    .sort((a, b) => a.daysUntil - b.daysUntil)
    .slice(0, 3);

  return {
    dots,
    expiredCount,
    totalShown: dots.length,
    pastDueCount,
    overflowByColumn,
    byBucket,
    topUpcoming,
  };
}
